import { AnalyticEventService } from '../analytic-event/analytic-event.service';
import { CallStatsService } from '../call-stats/call-stats.service';
import { IssueDetectionService } from '../issue-detection/issue-detection.service';
import { IssueRemediationService } from '../issue-remediation/issue-remediation.service';
import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';

import { CallIssueType } from '../../models/call-issue/call-issue';
import { Participant } from '../../models/participant/participant';

export interface ScreenShareState {
  shareVideoTrack: MediaStreamTrack;
  shareAudioTrack: MediaStreamTrack;
  sharing: boolean;
  shareLoading: boolean;
  mainStream: MediaStream;
  mainStreamId: string;
  pinned: string;
  userId: string;
}

export class ScreenShareManager {
  constructor(
    private analytics: AnalyticEventService,
    private callstats: CallStatsService,
    private issueDetection: IssueDetectionService,
    private issueRemediation: IssueRemediationService,
    private logging: LoggingService,
    private participantsList: ParticipantsListService,
    private getState: () => ScreenShareState,
    private setState: (patch: Partial<ScreenShareState>) => void,
    private publishCb: () => CallableFunction,
    private unpublishSharingCb: () => CallableFunction,
  ) {}

  async startSharing(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: {max: 1920},
          frameRate: {max: 24},
        },
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false,
          channelCount: {ideal: 2},
          sampleRate: {ideal: 96000},
          sampleSize: {ideal: 24},
        },
      });
      if (stream) {
        const state = this.getState();
        stream.getTracks().forEach((track: MediaStreamTrack) => {
          if (state.shareLoading) {
            track.stop();
          } else {
            if (track.kind === 'video') {
              this.setState({ shareVideoTrack: track });
              this.callstats.shareVideoTrackId = track.id;
              const settings = track.getSettings();
              if (settings.width === 0 || settings.height === 0 || settings.frameRate === 0) {
                this.analytics.logEvent('screenshare-track-settings-issue');
              }
              this.issueRemediation.setMaxProfile(settings.width || 1280, settings.height || 720, settings.frameRate || 24, true);
              this.issueRemediation.shareVideoTrack = track;
              this.issueRemediation.startSharing();
              this.publishCb()(track);
            }
            if (track.kind === 'audio') {
              this.setState({ shareAudioTrack: track });
              this.callstats.shareAudioTrackId = track.id;
              this.issueRemediation.shareAudioTrack = track;
              this.publishCb()(track);
            }
          }
        });

        if (!state.shareLoading) {
          this.analytics.logEvent('screenshare');
          this.setState({ sharing: true });
          this.setMainStream();
        } else {
          throw new Error('already_sharing');
        }
      }
    } catch (err) {
      if (err.message !== 'already_sharing') {
        this.issueDetection.logEvent(CallIssueType.screen_sharing_missing, null, true);
      }

      throw err;
    }
  }

  stopSharing(): void {
    const state = this.getState();
    if (state.sharing) {
      this.unpublishSharingCb()();
      if (state.shareAudioTrack) {
        this.callstats.shareAudioTrackId = null;
        state.shareAudioTrack.stop();
        this.setState({ shareAudioTrack: null });
      }
      if (state.shareVideoTrack) {
        this.callstats.shareVideoTrackId = null;
        state.shareVideoTrack.stop();
        this.setState({ shareVideoTrack: null });
      }
      this.issueRemediation.stopSharing();
      this.setState({ sharing: false });
      this.setMainStream();
    }
  }

  setSharing(track: MediaStreamTrack): void {
    const state = this.getState();
    if (track.kind === 'video') {
      if (state.shareVideoTrack) {
        this.setState({ shareVideoTrack: null });
        this.setMainStream();
      }
      this.setState({ shareVideoTrack: track });
      this.callstats.shareVideoTrackId = track.id;
    }
    if (track.kind === 'audio') {
      this.setState({ shareAudioTrack: track });
      this.callstats.shareAudioTrackId = track.id;
    }
    this.setMainStream();
    this.issueRemediation.setSharingProfiles();
  }

  unsetSharing(): void {
    const state = this.getState();
    if (!state.sharing) {
      if (state.shareVideoTrack) {
        this.callstats.shareVideoTrackId = null;
        state.shareVideoTrack.stop();
        this.setState({ shareVideoTrack: null });
      }
      if (state.shareAudioTrack) {
        this.callstats.shareAudioTrackId = null;
        state.shareAudioTrack.stop();
        this.setState({ shareAudioTrack: null });
      }
      this.issueRemediation.stopSharing();
      this.setMainStream();
    }
  }

  setMainStream(force?: boolean): void {
    const state = this.getState();
    this.setMainStreamId();

    if (!state.mainStream) {
      return;
    }

    if (!state.shareVideoTrack) {
      state.mainStream.getTracks().forEach((track: MediaStreamTrack) => {
        state.mainStream.removeTrack(track);
      });

      return;
    }

    this.setState({ mainStreamId: null });

    const oldVideo: MediaStreamTrack[] = state.mainStream.getVideoTracks();
    if (oldVideo.length > 0 && oldVideo[0].id === state.shareVideoTrack.id) {
      return;
    }

    this.logging.log('StreamService: Reload mainstream', LoggingChannels.Stream);
    state.mainStream.getTracks().forEach((track: MediaStreamTrack) => {
      state.mainStream.removeTrack(track);
    });

    state.mainStream.addTrack(state.shareVideoTrack);
    if (!state.sharing && state.shareAudioTrack) {
      state.mainStream.addTrack(state.shareAudioTrack);
    }
  }

  setMainStreamId(): void {
    const state = this.getState();
    let mainStreamId = state.mainStreamId;
    let lastSpokeTimestamp = 0;

    if (!mainStreamId) {
      mainStreamId = state.userId;
    }
    if (mainStreamId === state.userId || !this.participantsList.participants[mainStreamId]?.isActive) {
      const participants: Participant[] = this.participantsList.participantsArray;
      participants.forEach((participant: Participant) => {
        if (
          ((participant.isActive || mainStreamId === state.userId) &&
          participant.lastSpokeTimestamp >= lastSpokeTimestamp &&
          participant.id !== state.userId) ||
          participant.id === state.pinned
        ) {
          lastSpokeTimestamp = participant.lastSpokeTimestamp;
          mainStreamId = participant.id;
        }
      });
    }
    if (!this.participantsList.participants[mainStreamId]) {
      mainStreamId = state.userId;
    }

    this.setState({ mainStreamId });
  }
}
